import React, { Component } from "react";
import { Modal, Button } from "react-bootstrap";
import { Navigate } from "react-router-dom";

import moment from 'moment';

import { withRouter } from '../../../with-router';
import GameService from "../../../services/game";
import FirebaseService from "../../../services/firebase";

import GameTimeout from "../../../components/GameTimeout";
import TextModal from "../../../components/TextModal";
import ErrorModal from "../../../components/ErrorModal";
import SurvivorClockBar from "../../../components/SurvivorClockBar";
import { isWebPlay } from "../../../utils/webPlay";
import { periodAndDeadline } from "../../../utils/pickDeadline";
import { readRemoteSession } from "../../../context/RemoteLaunchContext";
import { exitGame } from "../../../utils/exitGame";

import './Survivor.css';
import { visitOver } from "../../../services/visit";

/**
 * Survivor Pool.
 *
 * Two screens in one component, because they are one flow and share all their
 * state:
 *
 *   SELECT — the player's tickets as a season grid, one row each. This is the
 *            screen the other game types have no analogue for: a player can
 *            hold several independent "lives", and has to say which one they
 *            are playing before anything else makes sense.
 *   PICK   — this week's matchups for the chosen ticket.
 *
 * Two things are deliberately not decided here:
 *
 * 1. **All player-facing copy comes from the server.** headline, subline and
 *    the new-entry block arrive resolved on the state payload. The rules behind
 *    them (daily limit, entry cap, no surviving tickets) live in one service so
 *    the admin preview and the kiosk cannot drift apart, and duplicating the
 *    state machine in JS would guarantee they eventually did.
 *
 * 2. **Refusals are the server's words too.** The Survivor branch of
 *    PostTicketsAPI answers with {code, message}; we show the message. A pick
 *    refused after kickoff or on an already-used team ends or shapes somebody's
 *    season, so "something went wrong" is not good enough.
 */
class Survivor extends Component {
    constructor(props) {
        super(props);

        this.state = {
            page_ready: false,
            redirect: null,
            venue: null,
            promo: null,
            player: null,
            survivor: null,          // the /survivor/pick payload
            ticket_id: null,         // the life being played; null = not started
            period_id: null,         // the week it is being played for
            confirm: null,           // {event, choice, opponent}
            ticket: null,            // the submitted ticket, for exitGame's result
            help_modal: false,
            how_to_play_modal: false,
            // Two kinds of failure, two modals. ErrorModal's only button is
            // "Return to Lobby", which is right when the promo cannot be played
            // at all and wrong for a refused pick -- there the player should
            // stay put and choose another team.
            error_modal: {show: false, title: "", text: ""},
            pick_error: {show: false, title: "", text: ""},
            block: false,
            // Five minutes on the web, one on a kiosk.
            //
            // A kiosk is a shared machine and an idle session holds it; a
            // player on their own phone is not blocking anybody, and Survivor
            // asks them to read a slate of games and think about it. One minute
            // was throwing web players out mid-decision — and out to the auth
            // lobby, which meant identifying themselves again to finish a pick.
            timeout: isWebPlay() ? 300000 : 60000
        };
        this.current_time = moment();
    }

    componentDidMount() {
        if(this.props.router.location.state == null){
            this.exitOrHome("error");
            return;
        }

        let venue = this.props.router.location.state.venue;
        let promo = this.props.router.location.state.promo;
        let player = this.props.router.location.state.player;

        // Black now, the configured image when loadPickScreen answers. Painting
        // immediately rather than waiting means no white flash on the way in,
        // and the authoritative URL comes from this screen's own endpoint --
        // never from the navigation state, which is a stub when the player
        // arrives from the entry manager.
        this.paintBackground(venue.pickscreen_background_image_url || "");

        FirebaseService.logEvent("play_promo_start", {"venue_id": venue.id, "card_num": player.card_number, "promo_id": promo.id});

        // Which life is being played, and for which week. The entry manager
        // sends both (SUR-431); this screen is a pick screen, not a second
        // selector, so it opens straight on the games.
        let ticket_id = this.props.router.location.state.survivor_ticket_id || null;
        let period_id = this.props.router.location.state.survivor_period_id || null;

        this.props.showModal("Loading...");
        this.setState({
            venue: venue, promo: promo, player: player,
            ticket_id: ticket_id, period_id: period_id
        }, () => this.loadPickScreen());
    }

    /** SUR-501..SUR-510 — one call, its own contract.
     *
     *  Not /players/play/. That answers the entry manager's question and
     *  carries none of what a pick needs; reading it here rendered this screen
     *  blank, because every field it asked for was absent and nothing threw. */
    loadPickScreen(done){
        GameService.getSurvivorPickScreen(
            this.state.player.id,
            this.state.promo.id,
            this.state.period_id,
            this.state.ticket_id
        ).then((data) => {
            this.props.hideModal();
            if(!data.events || data.events.length === 0){
                this.setState({
                    page_ready: false,
                    error_modal: {show: true, title: "Something went wrong!",
                                  text: "There are no games scheduled for this promotion right now.\nPlease return to the Lobby."}
                });
                return;
            }
            // The server resolved league-then-venue for us. Repainting on every
            // load is deliberate: the 30s-refresh case and the post-submit
            // reload both come back through here.
            this.paintBackground(data.background_image_url || "");
            this.setState({
                survivor: data,
                // GameTimeout reads promo.league.warning_image off this state,
                // and the entry manager's stub carries no league at all. Filled
                // from the server's answer rather than the navigation state, for
                // the same reason as the background above. Spread so the fuller
                // promo the lobby route passes keeps whatever else it had.
                promo: {
                    ...this.state.promo,
                    league: {
                        ...(this.state.promo && this.state.promo.league),
                        warning_image: data.warning_image || ""
                    }
                },
                ticket_id: data.ticket_id,
                period_id: data.promo_period_id,
                page_ready: true
            }, () => { if(done) done(); });
        }, (error) => {
            console.log(error);
            this.props.hideModal();
            if(visitOver(error, this)) return;   // WEB-413
            this.setState({
                error_modal: {show: true, title: "Something went wrong!",
                              text: "Please return to the Lobby and try again."}
            });
        });
    }

    /** Set in componentDidMount, cleared on the way out. Without the clearing
     *  half this screen's image outlived it and turned up under the entry
     *  manager, which is how the same screen came to look different depending
     *  on where the player had been. The colour is cleared for the same reason:
     *  the four other game types are light-themed screens and must not inherit
     *  a black canvas from this one. */
    componentWillUnmount() {
        document.body.style.backgroundImage = "";
        document.body.style.backgroundColor = "";
        document.body.classList.remove("survivor-bleed");
    }

    /** This screen's background: the configured image over an opaque black.
     *
     *  The black is not decoration. ``body`` is ``background-color:
     *  transparent`` (App.css), the lobby writes its image onto <html>, and the
     *  entry manager clears BOTH on its way out
     *  (PrePick.clearInheritedBackground). So a promotion with no configured
     *  image landed here over a cleared <html> and a transparent <body> and got
     *  the browser's white -- under white text, with the translucent panels
     *  turning grey over it.
     *
     *  That is why it read as a "later rounds" fault: it needs the player to
     *  arrive via the entry manager, which is the route they take once they have
     *  a scored week to look at. Straight from the lobby, the lobby's own image
     *  was still on <html> and showed through.
     *
     *  It goes on <body>, not on .survivor, because <body> is where the image
     *  lands -- a colour on .survivor would paint over an admin's background and
     *  hide it. Behind the image it costs an admin nothing: a configured image
     *  still wins, and one that does not cover the viewport now gets black
     *  around it instead of white.
     *
     *  #000 is .prepick's black on purpose, so stepping from the entry manager
     *  to the pick screen does not change colour underfoot.
     *
     *  SUR-470 -- the black stays the FLOOR and stops being the ceiling. It is
     *  still what a promotion with no configured image lands on, and still what
     *  fills around an image too small to cover. What changed is that the
     *  playfield column no longer paints its own black over the top: with art
     *  configured, the column goes transparent and the image runs the width of
     *  the page, showing between the events and around the edges. The panels
     *  themselves stay opaque -- text over turf was the reason they became
     *  opaque in the first place, and that has not stopped being true. */
    paintBackground(url){
        document.body.style.backgroundColor = "#000";
        document.body.style.backgroundImage = url ? `url(${url})` : "";
        // SUR-470 -- everything that lets the art through is scoped to this
        // class, so it lives and dies with this screen. The lobby, the entry
        // manager and every other game keep the panels they were designed
        // against; nothing here can leak into them.
        //
        // Only when there IS an image. Making the column transparent over no
        // artwork just exposes the grey page behind it, which is the thing
        // this change exists to stop showing.
        document.body.classList.toggle("survivor-bleed", !!url);
    }

    // ------------------------------------------------------------------
    // Pick screen
    // ------------------------------------------------------------------

    /** SUR-502 / SUR-510 — why a choice is greyed, in the words shown on it.
     *
     *  Both answers come from the server, which decided them from the ticket's
     *  own picks and the event's start time. Deriving either here would be a
     *  second opinion about a rule that ends somebody's season. */
    tapChoice(event, choice){
        if(this.state.block) return;

        if(choice.started){
            this.showPickError("Too late for this game", "That game has already started.\nPlease choose another.");
            return;
        }
        if(choice.used_in){
            this.showPickError("Already used", `You selected this in ${choice.used_in}.\nPlease choose another.`);
            return;
        }

        let opponent = (event.choices || []).find(c => c.choice_id !== choice.choice_id);
        this.setState({
            confirm: {
                event: event,
                choice: choice,
                opponent: opponent || null
            }
        });
    }

    confirmPick(){
        let confirm = this.state.confirm;
        if(!confirm || this.state.block) return;

        this.setState({ block: true });
        this.props.showModal("Loading...");

        GameService.postSurvivorTicket(
            this.state.player.id,
            this.state.promo.id,
            this.state.period_id,
            [{
                "eventid": confirm.event.id,
                "eventchoiceid": confirm.choice.event_choice_id,
                "choiceid": confirm.choice.choice_id
            }],
            moment().format(),
            // null starts a ticket: the server creates it and records this pick
            // in one transaction, so a ticket never exists without the pick
            // that justifies it (SUR-437).
            this.state.ticket_id
        ).then((data) => {
            FirebaseService.logEvent("play_promo_success", {"venue_id": this.state.venue.id, "card_num": this.state.player.card_number, "promo_id": this.state.promo.id, "ticket_id": data.ticket.id});
            this.props.hideModal();
            // Survivor ends a pick by going back to the entry manager, not by
            // showing a receipt on a countdown.
            //
            // A receipt and a "returning to lobby in 15…" belong to a game that
            // is over when the ticket prints. Survivor is a season: the player
            // has other lives to play, more weeks coming, and the thing they
            // actually want to see is their pick landing on the grid. Sending
            // them to a receipt and then out to the lobby made every pick feel
            // like the end of the visit.
            this.toPrePick();
        }, (error) => {
            this.props.hideModal();
            this.setState({ block: false, confirm: null });

            // The Survivor branch answers with a real reason. Show it — the
            // player needs to know whether they picked a started game, reused a
            // team, or hit the daily limit, because those need different
            // actions from them.
            let body = error && error.response && error.response.data;
            if(body && body.message){
                this.showPickError("Pick not accepted", body.message);
            }else{
                this.showPickError("Something went wrong!", "We could not record that pick.\nPlease try again.");
            }
            // Re-read: a refusal usually means the screen's picture is stale —
            // a game kicked off, or another ticket used today's play.
            this.loadPickScreen();
        });
    }

    // ------------------------------------------------------------------
    // Exit
    //
    // Unlike the other four game screens there is no receipt path here:
    // shouldPrintReceipt(), onSuccessScreen() and startSuccessModalTimer() are
    // gone with the success modal. Survivor does not end when a ticket prints —
    // the player has other lives to play and more weeks coming — so a pick
    // returns to the entry manager. The receipt is emailed instead, when the
    // promo asks for it (Promo.email_receipts_to_players).
    // ------------------------------------------------------------------

    /** Back to the entry manager, for the same player.
     *
     *  Where a Survivor pick ends, and where a Survivor timeout ends too. The
     *  route state carries venue and player, which is what PrePick needs to
     *  load; it re-reads the grid on mount, so the pick just made is on screen
     *  when the player arrives rather than a render behind.
     *
     *  `key` forces a fresh mount even when the player is already notionally
     *  "on" pre-pick, so returning to it can never show a cached grid. */
    /** The pool board, on its own screen. It used to be a modal here fed by a
     *  second endpoint; one board in one place is enough, and it is the one the
     *  entry manager links to as well. */
    toLeaderboard(){
        this.setState({
            redirect: {
                path: "/survivor/leaderboard",
                state: {
                    venue: this.state.venue,
                    player: this.state.player
                }
            }
        });
    }

    toPrePick(){
        this.setState({
            block: false,
            confirm: null,
            redirect: {
                path: "/survivor/prepick",
                state: {
                    venue: this.state.venue,
                    player: this.state.player,
                    reloaded_at: Date.now()
                }
            }
        });
    }

    exitOrHome(result){
        exitGame({
            remoteCtx: readRemoteSession(),
            setRedirect: (redirect) => this.setState({ redirect: redirect }),
            result: result,
            player: this.state.player,
            ticket: this.state.ticket ? { id: this.state.ticket.id, promo_id: this.state.promo && this.state.promo.id } : null
        });
    }

    /** A refused pick. Dismissible: the player picks something else. */
    showPickError(title, text){
        this.setState({ pick_error: {show: true, title: title, text: text} });
    }

    closePickError(){
        this.setState({ pick_error: {show: false, title: "", text: ""} });
    }

    /** Give the player longer to read a text modal, matching the other screens. */
    onToggleTextModal(new_state){
        this.setState({ timeout: new_state ? 60000*3 : 60000 });
    }

    // ------------------------------------------------------------------
    // Render
    // ------------------------------------------------------------------

    /* renderSelect() is gone, along with renderGridRow().
     *
     * Choosing which ticket to play is the entry manager's job (SUR-401,
     * SUR-403, SUR-431): it shows every life across every week and puts PLAY on
     * the one that can act. A second selector here duplicated that badly — and
     * it read fields that no payload has produced since the SUR-4xx rewrite, so
     * it rendered an empty div and took the screen down with it.
     *
     * This screen now opens on the games for the ticket it was handed.
     */
    renderPick(){
        let survivor = this.state.survivor;
        if(!survivor) return null;
        let season = survivor.season || [];

        return (
            <div className="survivor-pick">
                <div className="survivor-pick-head">
                    {/* Back to the entry manager, not out of the game. A player
                        who opened the wrong ticket wants their grid, not the
                        lobby and another identification. */}
                    <Button className="survivor-back" onClick={() => this.toPrePick()}>← Tickets</Button>
                    <h1>{survivor.ticket_label}</h1>
                    {/* SUR-441 — the same line the entry manager carries, so
                        the deadline reads identically on both screens. */}
                    <p className="survivor-period">
                        {periodAndDeadline(
                            survivor.promo_period_name, survivor.entry_end_time
                        )}
                    </p>
                    <p className="survivor-subline">Pick one team to survive the week.</p>
                    {/* A refusal the server already knows about — an eliminated
                        ticket, a closed window, today's play spent. Said once,
                        here, rather than discovered on tapping a team. */}
                    {survivor.blocked_message
                        ? <p className="survivor-clockwarn">{survivor.blocked_message}</p>
                        : null}
                </div>

                <div className="survivor-pick-body">
                    <div className="survivor-events">
                        {(survivor.events || []).map((event) => (
                            <div key={event.id} className={`survivor-event${event.started ? " survivor-event-started" : ""}`}>
                                <div className="survivor-event-time">
                                    {moment(event.starts_at).format("ddd h:mm A")}
                                    {event.started ? <span className="survivor-started-note"> · started</span> : null}
                                </div>
                                <div className="survivor-event-choices">
                                    {(event.choices || []).map((choice) => (
                                        <div key={choice.event_choice_id}
                                             className={`survivor-choice${choice.used_in ? " survivor-choice-used" : ""}${choice.started ? " survivor-choice-started" : ""}`}
                                             onClick={() => this.tapChoice(event, choice)}>
                                            {choice.logo_url
                                                ? <img src={choice.logo_url} alt={choice.name} />
                                                : null}
                                            <span className="survivor-choice-name">
                                                {choice.city_name} {choice.name}
                                            </span>
                                            {/* SUR-510 — every greyed option says why.
                                                "Week N" for a team already used, "IN PLAY"
                                                for a started game. Shown rather than hidden
                                                so the player can see their own season at a
                                                glance instead of wondering where a team went. */}
                                            {choice.used_in ? (
                                                <div className="survivor-shield">
                                                    <span className="survivor-shield-text">Victorious<br/>{choice.used_in}</span>
                                                </div>
                                            ) : choice.started ? (
                                                <div className="survivor-shield">
                                                    <span className="survivor-shield-text">IN PLAY</span>
                                                </div>
                                            ) : null}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* SUR-503 — every pick on the ticket, present on load. In
                        week six this shows five teams before anything is tapped. */}
                    <aside className="survivor-rail">
                        <h2>Your season</h2>
                        {season.length === 0
                            ? <p className="survivor-rail-empty">This is your first pick.</p>
                            : season.map((pick, i) => (
                                <div key={i} className={`survivor-rail-item survivor-cell-${pick.outcome}`}>
                                    <span className="survivor-rail-week">{pick.period_name}</span>
                                    {pick.logo_url ? <img src={pick.logo_url} alt={pick.choice_name} /> : null}
                                    <span className="survivor-rail-team">{pick.choice_name}</span>
                                </div>
                            ))}
                    </aside>
                </div>
            </div>
        );
    }

    renderConfirm(){
        let confirm = this.state.confirm;
        if(!confirm) return null;
        let survivor = this.state.survivor;

        return (
            <Modal show={true} centered className="survivor-confirm" onHide={() => this.setState({confirm: null})}>
                <Modal.Body>
                    <div className="survivor-confirm-teams">
                        {confirm.choice.logo_url
                            ? <img className="survivor-confirm-pick" src={confirm.choice.logo_url} alt={confirm.choice.name} />
                            : null}
                        {confirm.opponent && confirm.opponent.logo_url
                            ? <img className="survivor-confirm-opp" src={confirm.opponent.logo_url} alt={confirm.opponent.name} />
                            : null}
                    </div>
                    <h2 className="survivor-confirm-line">
                        {confirm.choice.name} over {confirm.opponent ? confirm.opponent.name : "their opponent"}
                        <span className="survivor-confirm-week"> — {survivor.promo_period_name}</span>
                    </h2>
                    <div className="survivor-confirm-actions">
                        <Button className="survivor-confirm-yes" disabled={this.state.block}
                                onClick={() => this.confirmPick()}>Confirm</Button>
                        <Button className="survivor-confirm-no" disabled={this.state.block}
                                onClick={() => this.setState({confirm: null})}>Pick Again</Button>
                    </div>
                </Modal.Body>
            </Modal>
        );
    }

    render() {
        if(this.state.redirect) return <Navigate to={this.state.redirect.path} state={this.state.redirect.state} />;

        return (
            <div className="survivor main-container">
                {/* Banner only — the clock is set on the entry manager. Shown
                    here so a moved clock cannot be missed on the screen where
                    picks are actually made. */}
                <SurvivorClockBar />
                {/* Timing out of a pick returns to the entry manager, not to
                    the lobby. The player identified themselves to get here;
                    sending them back to the auth screen because they took too
                    long over a slate of games makes them do it again to finish
                    the pick they were already making. */}
                {this.state.page_ready ? (
                    <GameTimeout {...this} timeout={this.state.timeout}
                                 onTimeout={() => this.toPrePick()} />
                ) : null}

                {/* Straight to the games. Which ticket is being played was
                    decided in the entry manager (SUR-431) and arrives in the
                    route state; there is no second selector here. */}
                {this.state.page_ready ? this.renderPick() : null}

                {this.renderConfirm()}

                {this.state.page_ready ? (
                    <div className="survivor-footer">
                        <Button className="survivor-link" onClick={() => this.setState({how_to_play_modal: true})}>How To Play</Button>
                        <Button className="survivor-link" onClick={() => this.setState({help_modal: true})}>Help</Button>
                        {/* The same board the entry manager links to, rather
                            than a second one in a modal here. */}
                        <Button className="survivor-link" onClick={() => this.toLeaderboard()}>Pool Standings</Button>
                        {/* Exit goes back to the tickets, not out of the game.
                            The player identified themselves to get here. */}
                        <Button className="survivor-link" onClick={() => this.toPrePick()}>Exit</Button>
                    </div>
                ) : null}

                {/* Text off the pick payload, not off the promo: the entry
                    manager hands over an id/name/game_type stub, so these were
                    undefined and TextModal's split() on undefined took the
                    whole page down. `|| ""` because the modal has no defence of
                    its own and a promo may legitimately have neither. */}
                {this.state.page_ready && this.state.survivor ? (
                    <div>
                        <TextModal onToggle={(new_state) => this.onToggleTextModal(new_state)}
                                   title="Help" class="help_modal"
                                   help_modal={this.state.help_modal}
                                   text={this.state.survivor.help_rules || ""} />
                        <TextModal onToggle={(new_state) => this.onToggleTextModal(new_state)}
                                   title="How To Play" class="how_to_play_modal"
                                   help_modal={this.state.how_to_play_modal}
                                   text={this.state.survivor.how_to_play || ""} />
                    </div>
                ) : null}

                {/* Fatal only — its single action is Return to Lobby. */}
                <ErrorModal show={this.state.error_modal.show}
                            title={this.state.error_modal.title}
                            text={this.state.error_modal.text}
                            restart={this.state.error_modal.restart} />

                {/* A refused pick. Dismiss and choose again. */}
                <Modal show={this.state.pick_error.show} centered className="survivor-pick-error"
                       onHide={() => this.closePickError()}>
                    <Modal.Header>
                        <Modal.Title className="w-100 text-center">{this.state.pick_error.title}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {this.state.pick_error.text.split(/\n/).map((line, i) => (
                            <p key={i} className="text-center mb-1">{line}</p>
                        ))}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="primary" onClick={() => this.closePickError()}>Choose another</Button>
                    </Modal.Footer>
                </Modal>

                {/* The standings modal is gone with openStandings(). It was a
                    second board, in a second format, off a second endpoint —
                    club numbers in a list, beside a grid that says the same
                    thing better. Pool Standings now opens the one board
                    (SUR-460), which the entry manager links to as well. */}

                {/* No success modal, no receipt, no "returning to the lobby in
                    15s". A submitted pick goes straight back to the entry
                    manager — see toPrePick(). The receipt itself is not lost:
                    it is emailed if the promo asks for it
                    (Promo.email_receipts_to_players). */}
            </div>
        );
    }
}

export default withRouter(Survivor);
